import { z } from "zod";
import {
  FLASHCARD_DAY_START_WINDOW_MS,
  FLASHCARD_REVIEWS_BATCH_MAX,
  FlashcardCardId,
  FlashcardCardState,
  FlashcardDeckId,
  FlashcardDeckSummary,
  FlashcardOwnerCard,
  FlashcardRating,
  FlashcardReviewId,
  FlashcardReviewMode,
  FlashcardWordsDeck,
} from "./flashcards.ts";
import { LocalDate } from "./schedule.ts";

/**
 * Карточки глазами получателя: колода с состоянием, `today`, досылка
 * повторений, и сводка по ученику для преподавателя (9.1, 9.5
 * `docs/flashcards-design.md`). Корневой экспорт, сосед `./flashcards.ts`.
 *
 * ⚠️ Здесь только схемы и одна функция прижатия: `ts-fsrs` и очередь живут за
 * подпутём `@edu/shared/flashcards`. Модуль досылки очереди (7.4) читает
 * `FlashcardReviewsRequest` отсюда и в стартовом чанке.
 */

/* ──────────────────────────── колода получателя ────────────────── */

/**
 * Карточка в глазах получателя: без `origin` (3.2), со своим состоянием и
 * датами занятий, к которым она привязана (`occurs_on`, 3.2): из них очередь
 * узнаёт карточки последнего занятия и порядок новых слов (4.3).
 *
 * `firstReviewAt` это наименьший `reviewed_at` журнала по карточке: по нему
 * очередь сама считает, сколько новых введено с начала дня, и потолок держится
 * внутри сессии, после каждой оценки, а не только по счёту из `today`.
 */
export const FlashcardStudentCard = z.object({
  id: FlashcardCardId,
  position: z.number().int(),
  front: z.string(),
  back: z.string(),
  example: z.string().nullable(),
  occursOn: z.array(LocalDate),
  state: FlashcardCardState,
  firstReviewAt: z.number().int().nullable(),
});
export type FlashcardStudentCard = z.infer<typeof FlashcardStudentCard>;

/**
 * `GET /v1/decks/:deckId`. Владелец получает колоду целиком, получатель свою
 * половину. `lastOccurrenceOn` это одно значение на ПАРУ по всем живым частям
 * колоды слов, одинаковое у каждой части, в поясе пары (4.3); у обычной колоды
 * `null`.
 */
export const FlashcardDeckResponse = z.discriminatedUnion("view", [
  z.object({
    view: z.literal("owner"),
    deck: FlashcardDeckSummary,
    cards: z.array(FlashcardOwnerCard),
  }),
  z.object({
    view: z.literal("recipient"),
    id: FlashcardDeckId,
    title: z.string(),
    rev: z.number().int(),
    teacherName: z.string(),
    isWords: z.boolean(),
    newPerDay: z.number().int().nullable(),
    assignedAt: z.number().int(),
    browsedAt: z.number().int().nullable(),
    lastOccurrenceOn: LocalDate.nullable(),
    cards: z.array(FlashcardStudentCard),
  }),
]);
export type FlashcardDeckResponse = z.infer<typeof FlashcardDeckResponse>;

/* ──────────────────────────── today ────────────────────────────── */

/** `GET /v1/flashcards/today?dayStart=`: начало дня ученика, мс эпохи (4.3). */
export const FlashcardsTodayQuery = z.object({ dayStart: z.coerce.number().int() });
export type FlashcardsTodayQuery = z.infer<typeof FlashcardsTodayQuery>;

/**
 * Прижатие `dayStart` (4.3): к окну от «сейчас минус 36 часов» до «сейчас».
 * Часы устройства не доверенные, а начало дня позже «сейчас» или старше полутора
 * суток значит не другой пояс, а сбитые часы.
 */
export function clampDayStart(dayStart: number, now: number): number {
  return Math.min(now, Math.max(now - FLASHCARD_DAY_START_WINDOW_MS, dayStart));
}

/**
 * Строка `today` по открытой ученику выдаче. «Непустой» значит сумма `dueNow`
 * больше нуля. Кэш колоды на устройстве протухает, когда `rev` разошёлся (4.3).
 * `dueNow` считается без потолка показов сессии: это счёт на день.
 *
 * `firstReviewsSince` оставлен справкой для экрана («сегодня начато N»): потолок
 * новых его НЕ читает, очередь считает введённые сама по `firstReviewAt`.
 */
export const FlashcardsTodayDeck = z.object({
  deckId: FlashcardDeckId,
  title: z.string(),
  isWords: z.boolean(),
  rev: z.number().int(),
  teacherName: z.string(),
  dueNow: z.number().int().min(0),
  firstReviewsSince: z.number().int().min(0),
});
export type FlashcardsTodayDeck = z.infer<typeof FlashcardsTodayDeck>;

export const FlashcardsTodayResponse = z.object({ decks: z.array(FlashcardsTodayDeck) });
export type FlashcardsTodayResponse = z.infer<typeof FlashcardsTodayResponse>;

/* ──────────────────────────── повторения ───────────────────────── */

/** Повторение из очереди устройства (7.3). `reviewedAt` часы устройства, мс. */
export const FlashcardReviewInput = z.object({
  id: FlashcardReviewId,
  cardId: FlashcardCardId,
  rating: FlashcardRating,
  mode: FlashcardReviewMode,
  reviewedAt: z.number().int(),
});
export type FlashcardReviewInput = z.infer<typeof FlashcardReviewInput>;

/** `POST /v1/flashcards/reviews`. */
export const FlashcardReviewsRequest = z.object({
  reviews: z.array(FlashcardReviewInput).min(1).max(FLASHCARD_REVIEWS_BATCH_MAX),
});
export type FlashcardReviewsRequest = z.infer<typeof FlashcardReviewsRequest>;

/**
 * Итог построчно. `dropped` один на все причины (чужая карточка, корзина,
 * отозванная выдача), чтобы не подсказывать перебирающему id (7.3).
 */
export const FlashcardReviewsResponse = z.object({
  results: z.array(
    z.object({ id: FlashcardReviewId, status: z.enum(["accepted", "duplicate", "dropped"]) }),
  ),
});
export type FlashcardReviewsResponse = z.infer<typeof FlashcardReviewsResponse>;

/* ──────────────────────────── сводка ───────────────────────────── */

/**
 * `GET /v1/students/:userId/decks`: сводка в карточке ученика (9.5). Три числа,
 * а не доля; «выучено» это повторение с интервалом от `FLASHCARD_LEARNED_DAYS`
 * (`cardProgress` в `./flashcards-schedule.ts`), `fresh` это новые.
 */
export const StudentDeckProgress = z.object({
  deckId: FlashcardDeckId,
  title: z.string(),
  words: FlashcardWordsDeck.nullable(),
  revoked: z.boolean(),
  learned: z.number().int().min(0),
  learning: z.number().int().min(0),
  fresh: z.number().int().min(0),
  lastReviewAt: z.number().int().nullable(),
  browsedAt: z.number().int().nullable(),
});
export type StudentDeckProgress = z.infer<typeof StudentDeckProgress>;

export const StudentDecksResponse = z.object({ decks: z.array(StudentDeckProgress) });
export type StudentDecksResponse = z.infer<typeof StudentDecksResponse>;
